import type * as THREE from 'three';
import type { Vec3 } from '../api/types.js';
import type { PostFxOptions } from './post-fx.js';
import type {
  CameraState,
  EngineId,
  InitOptions,
  LoadOptions,
  Projected,
  RaycastHit,
  ViewerCapabilities,
  ViewerEventDetailMap,
  ViewportSize,
} from './types.js';

export interface CameraFit {
  readonly pos: Vec3;
  readonly lookAt: Vec3;
  readonly fov: number;
}

export interface CapturedFrame {
  readonly blob: Blob;
  revoke(): void;
}

export interface ViewerAdapter {
  readonly capabilities: ViewerCapabilities;
  readonly events: EventTarget;

  init(opts: InitOptions): Promise<void>;
  loadSplatScene(url: string, opts?: LoadOptions): Promise<void>;
  getThreeScene(): THREE.Scene;
  getCamera(): THREE.PerspectiveCamera;
  getRenderer(): THREE.WebGLRenderer;
  setCamera(
    pos: readonly [number, number, number],
    lookAt: readonly [number, number, number],
    fov?: number,
  ): void;

  readonly raycast?: (screenX: number, screenY: number) => THREE.Vector3 | null;
  readonly enterXR?: (mode: 'vr' | 'ar') => Promise<void>;
  readonly exitXR?: () => Promise<void>;
  readonly dispose?: () => Promise<void>;
  readonly removeSplatScene?: (sceneIndex: number) => Promise<void>;
  // Per-frame hook called before viewer.update()/render(). Used by 4.C
  // OrbitControls (controls.update() each frame). Pass `null` to clear.
  readonly setBeforeRender?: (cb: (() => void) | null) => void;

  // 4.E: subscription to per-frame ticks (for FPS monitor). Returns unsubscribe.
  readonly onFrame?: (cb: (now: number) => void) => () => void;

  // 4.E: schedules a one-shot capture after the next render. Resolves with PNG
  // blob. Implementation MUST capture synchronously inside the same RAF tick
  // following viewer.render() to avoid the browser clearing the drawing buffer
  // (no preserveDrawingBuffer always-on, see SPIKE_4C0_REPORT.md + STEP4E).
  readonly captureFrame?: () => Promise<CapturedFrame>;

  // 4.E: compute auto-fit camera from current splat mesh bbox. Returns null
  // if the mesh is unavailable or has zero extent (caller falls back to default).
  readonly computeFitCamera?: () => CameraFit | null;

  // Inc B: compute an elevated "dollhouse" overview camera from the splat bbox.
  // Stessa capability gate (`cameraFit`) di computeFitCamera; null se la mesh è
  // assente o ha estensione zero.
  readonly computeOverviewCamera?: () => CameraFit | null;

  // Inc D — post-FX. setPostFx(true) lazily builds the EffectComposer pipeline
  // on first enable and switches renderTick to composer.render(); setPostFx(false)
  // bypasses it (composer kept alive). `opts` tunes bloom/grade/vignette.
  // NO-OP / bypass while in any XR state (gated in renderTick).
  readonly setPostFx?: (enabled: boolean, opts?: PostFxOptions) => void;
  // Inc D — keep the composer render targets in sync with the canvas. Called by
  // the orchestrator's single ResizeObserver right after renderer.setSize().
  readonly resizePostFx?: (width: number, height: number, pixelRatio?: number) => void;

  // FASE 1 PR2 — engine-neutral API (additivi OPZIONALI). PR3 sostituira' i
  // 19 call site `adapter.getCamera()/getThreeScene()/getRenderer()` in
  // orchestrator.ts; PR4 wire hotspot/measure.
  readonly engine?: EngineId;
  readonly getCameraState?: () => CameraState;
  readonly setCameraState?: (state: CameraState) => void;
  readonly getViewport?: () => ViewportSize;
  readonly projectWorldToScreen?: (world: Vec3, viewport?: ViewportSize) => Projected;
  readonly raycastHit?: (screenX: number, screenY: number) => RaycastHit | null;
  readonly resize?: (size: ViewportSize) => void;
}

export type ViewerAdapterEvent<K extends keyof ViewerEventDetailMap> = CustomEvent<
  ViewerEventDetailMap[K]
>;

type RaycastFn = NonNullable<ViewerAdapter['raycast']>;
type EnterXRFn = NonNullable<ViewerAdapter['enterXR']>;
type ExitXRFn = NonNullable<ViewerAdapter['exitXR']>;
type DisposeFn = NonNullable<ViewerAdapter['dispose']>;
type RemoveSplatSceneFn = NonNullable<ViewerAdapter['removeSplatScene']>;
type SetBeforeRenderFn = NonNullable<ViewerAdapter['setBeforeRender']>;
type OnFrameFn = NonNullable<ViewerAdapter['onFrame']>;
type CaptureFrameFn = NonNullable<ViewerAdapter['captureFrame']>;
type ComputeFitCameraFn = NonNullable<ViewerAdapter['computeFitCamera']>;
type ComputeOverviewCameraFn = NonNullable<ViewerAdapter['computeOverviewCamera']>;

export type WithRaycast = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly raycast: true };
  readonly raycast: RaycastFn;
};

export type WithEnterXR = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly enterXR: true };
  readonly enterXR: EnterXRFn;
};

export type WithExitXR = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly exitXR: true };
  readonly exitXR: ExitXRFn;
};

export type WithDispose = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly dispose: true };
  readonly dispose: DisposeFn;
};

export type WithRemoveSplatScene = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly removeSplatScene: true };
  readonly removeSplatScene: RemoveSplatSceneFn;
};

export type WithBeforeRender = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly beforeRenderHook: true };
  readonly setBeforeRender: SetBeforeRenderFn;
};

export type WithFrameCallbacks = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly frameCallbacks: true };
  readonly onFrame: OnFrameFn;
};

export type WithCaptureFrame = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly captureFrame: true };
  readonly captureFrame: CaptureFrameFn;
};

export type WithCameraFit = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly cameraFit: true };
  readonly computeFitCamera: ComputeFitCameraFn;
};

export function canRaycast(adapter: ViewerAdapter): adapter is WithRaycast {
  return adapter.capabilities.raycast === true && typeof adapter.raycast === 'function';
}

export function canEnterXR(adapter: ViewerAdapter): adapter is WithEnterXR {
  return adapter.capabilities.enterXR === true && typeof adapter.enterXR === 'function';
}

export function canExitXR(adapter: ViewerAdapter): adapter is WithExitXR {
  return adapter.capabilities.exitXR === true && typeof adapter.exitXR === 'function';
}

export function canDispose(adapter: ViewerAdapter): adapter is WithDispose {
  return adapter.capabilities.dispose === true && typeof adapter.dispose === 'function';
}

export function canRemoveSplatScene(adapter: ViewerAdapter): adapter is WithRemoveSplatScene {
  return (
    adapter.capabilities.removeSplatScene === true && typeof adapter.removeSplatScene === 'function'
  );
}

export function canSetBeforeRender(adapter: ViewerAdapter): adapter is WithBeforeRender {
  return (
    adapter.capabilities.beforeRenderHook === true && typeof adapter.setBeforeRender === 'function'
  );
}

export function canSubscribeFrames(adapter: ViewerAdapter): adapter is WithFrameCallbacks {
  return adapter.capabilities.frameCallbacks === true && typeof adapter.onFrame === 'function';
}

export function canCaptureFrame(adapter: ViewerAdapter): adapter is WithCaptureFrame {
  return adapter.capabilities.captureFrame === true && typeof adapter.captureFrame === 'function';
}

export function canFitCamera(adapter: ViewerAdapter): adapter is WithCameraFit {
  return adapter.capabilities.cameraFit === true && typeof adapter.computeFitCamera === 'function';
}

// Inc B: overview riusa la capability `cameraFit` (stesso bbox dello splat) ma
// con un type guard separato per non vincolare canFitCamera all'auto-fit.
export type WithOverviewCamera = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly cameraFit: true };
  readonly computeOverviewCamera: ComputeOverviewCameraFn;
};

export function canComputeOverviewCamera(adapter: ViewerAdapter): adapter is WithOverviewCamera {
  return (
    adapter.capabilities.cameraFit === true && typeof adapter.computeOverviewCamera === 'function'
  );
}

// Inc D — post-FX. Guarded by the `postFx` capability + both methods present.
type SetPostFxFn = NonNullable<ViewerAdapter['setPostFx']>;
type ResizePostFxFn = NonNullable<ViewerAdapter['resizePostFx']>;

export type WithPostFx = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly postFx: true };
  readonly setPostFx: SetPostFxFn;
  readonly resizePostFx: ResizePostFxFn;
};

export function canSetPostFx(adapter: ViewerAdapter): adapter is WithPostFx {
  return (
    adapter.capabilities.postFx === true &&
    typeof adapter.setPostFx === 'function' &&
    typeof adapter.resizePostFx === 'function'
  );
}

// FASE 1 PR2 — type guard per API engine-neutral. Adapter promette TUTTI i 6
// metodi quando capability `engineNeutralCamera` e' true. PR3 usera' questo
// guard prima di sostituire i call site `getCamera()/getRenderer()`.
type GetCameraStateFn = NonNullable<ViewerAdapter['getCameraState']>;
type SetCameraStateFn = NonNullable<ViewerAdapter['setCameraState']>;
type GetViewportFn = NonNullable<ViewerAdapter['getViewport']>;
type ProjectWorldToScreenFn = NonNullable<ViewerAdapter['projectWorldToScreen']>;
type RaycastHitFn = NonNullable<ViewerAdapter['raycastHit']>;
type ResizeFn = NonNullable<ViewerAdapter['resize']>;

export type WithEngineNeutralCamera = ViewerAdapter & {
  readonly capabilities: ViewerCapabilities & { readonly engineNeutralCamera: true };
  readonly engine: EngineId;
  readonly getCameraState: GetCameraStateFn;
  readonly setCameraState: SetCameraStateFn;
  readonly getViewport: GetViewportFn;
  readonly projectWorldToScreen: ProjectWorldToScreenFn;
  readonly raycastHit: RaycastHitFn;
  readonly resize: ResizeFn;
};

export function canUseEngineNeutralCamera(
  adapter: ViewerAdapter,
): adapter is WithEngineNeutralCamera {
  return (
    adapter.capabilities.engineNeutralCamera === true &&
    adapter.engine !== undefined &&
    typeof adapter.getCameraState === 'function' &&
    typeof adapter.setCameraState === 'function' &&
    typeof adapter.getViewport === 'function' &&
    typeof adapter.projectWorldToScreen === 'function' &&
    typeof adapter.raycastHit === 'function' &&
    typeof adapter.resize === 'function'
  );
}
